import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { useAuth } from '../../context/AuthContext';
import { usePasscode } from '../../components/common/PasscodeModal';

const CATEGORIES = ['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'];

// Only cash/bank assets (1000-1025) and current liabilities (2000-2099, e.g. credit cards)
// can ever be a payment source — see CLAUDE.md "Paid From: bank/cash accounts only (< 1030)".
const isCashBank = (category, code) => category === 'ASSET' && code >= 1000 && code <= 1025;
const isCurrentLiability = (category, code) => category === 'LIABILITY' && code >= 2000 && code <= 2099;
const canBePaymentSource = (category, accountCode) => {
  const code = parseInt(accountCode, 10);
  return isCashBank(category, code) || isCurrentLiability(category, code);
};

export default function AccountList() {
  const { role } = useAuth();
  const isAdmin = role === 'admin' || role === 'sadmin';
  const [accounts, setAccounts] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ category: '', account_type_id: '', account_name: '', description: '', account_code: '', is_payment_source: false });
  const [saving, setSaving] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false);
  const [confirmToggle, setConfirmToggle] = useState(null);
  const { prompt: promptPasscode, PasscodeModal } = usePasscode();

  useEffect(() => {
    supabase.from('chart_of_accounts').select('*, account_types(category, name)').order('account_code')
      .then(({ data }) => setAccounts((data || []).map(a => ({ ...a, category: a.account_types?.category || 'OTHER' }))))
      .finally(() => setLoading(false));
    supabase.from('account_types').select('*').order('code_range_start')
      .then(({ data }) => setAccountTypes(data || []));
  }, []);

  const typesForCategory = accountTypes.filter(t => t.category === form.category);

  async function onTypeChange(typeId) {
    const type = accountTypes.find(t => t.id === typeId);
    if (!type) { setForm(f => ({ ...f, account_type_id: typeId, account_code: '' })); return; }
    setForm(f => ({ ...f, account_type_id: typeId, account_code: '' }));
    setCodeLoading(true);
    const { data } = await supabase.from('chart_of_accounts')
      .select('account_code')
      .gte('account_code', String(type.code_range_start))
      .lte('account_code', String(type.code_range_end))
      .order('account_code', { ascending: false })
      .limit(1);
    const maxCode = data?.[0]?.account_code ? parseInt(data[0].account_code, 10) : null;
    const nextCode = maxCode !== null ? maxCode + 1 : type.code_range_start;
    setForm(f => ({ ...f, account_type_id: typeId, account_code: String(nextCode) }));
    setCodeLoading(false);
  }

  const handleAdd = async () => {
    if (!form.account_type_id || !form.account_name || !form.account_code) {
      alert('Account type, name and code are required.'); return;
    }
    setSaving(true);
    try {
      await supabase.from('chart_of_accounts').insert({
        account_code: form.account_code,
        account_name: form.account_name,
        description: form.description,
        account_type_id: form.account_type_id,
        is_payment_source: form.is_payment_source,
      });
      const { data } = await supabase.from('chart_of_accounts').select('*, account_types(category, name)').order('account_code');
      setAccounts((data || []).map(a => ({ ...a, category: a.account_types?.category || 'OTHER' })));
      setShowForm(false);
      setForm({ category: '', account_type_id: '', account_name: '', description: '', account_code: '', is_payment_source: false });
    } catch (err) { alert('Error saving account'); }
    finally { setSaving(false); }
  };

  const togglePaymentSource = async (acc, next) => {
    const prev = acc.is_payment_source;
    setAccounts(accs => accs.map(a => a.id === acc.id ? { ...a, is_payment_source: next } : a));
    const { error } = await supabase.from('chart_of_accounts').update({ is_payment_source: next }).eq('id', acc.id);
    if (error) setAccounts(accs => accs.map(a => a.id === acc.id ? { ...a, is_payment_source: prev } : a));
  };

  const confirmTogglePaymentSource = async () => {
    try { await promptPasscode(); } catch { return; }
    togglePaymentSource(confirmToggle.acc, confirmToggle.next);
    setConfirmToggle(null);
  };

  const filtered = accounts.filter(a => !q || a.account_code.includes(q) || a.account_name.toLowerCase().includes(q.toLowerCase()) || a.category?.toLowerCase().includes(q.toLowerCase()));

  const grouped = filtered.reduce((g, a) => { const cat = a.category||'OTHER'; if (!g[cat]) g[cat] = []; g[cat].push(a); return g; }, {});

  const categoryOrder = ['ASSET','LIABILITY','EQUITY','INCOME','EXPENSE'];
  const categoryColor = { ASSET:'badge-blue', LIABILITY:'badge-red', EQUITY:'badge-purple', INCOME:'badge-green', EXPENSE:'badge-yellow' };

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">Chart of Accounts</div><div className="page-subtitle">All accounts — click any to view full ledger</div></div>
        <button className="btn btn-primary" onClick={() => { setShowForm(s => !s); setForm({ category: '', account_type_id: '', account_name: '', description: '', account_code: '', is_payment_source: false }); }}>+ Add Account</button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 16, padding: 24 }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>New Account</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
            <div className="form-group">
              <label className="form-label">Category *</label>
              <select className="form-input" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value, account_type_id: '', account_code: '' }))}>
                <option value="">— Select —</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0) + c.slice(1).toLowerCase()}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Account Type *</label>
              <select className="form-input" value={form.account_type_id} onChange={e => onTypeChange(e.target.value)} disabled={!form.category}>
                <option value="">— Select —</option>
                {typesForCategory.map(t => <option key={t.id} value={t.id}>{t.name} ({t.code_range_start}–{t.code_range_end})</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Account Code</label>
              <input className="form-input" value={codeLoading ? 'Calculating…' : form.account_code} readOnly style={{ background: 'var(--bg-subtle, #f9fafb)', cursor: 'not-allowed', color: 'var(--text-muted)' }} placeholder="Auto-assigned" />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / 3' }}>
              <label className="form-label">Account Name *</label>
              <input className="form-input" value={form.account_name} onChange={e => setForm(f => ({ ...f, account_name: e.target.value }))} placeholder="e.g. Marketing Expenses" />
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <input className="form-input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            {isAdmin && canBePaymentSource(form.category, form.account_code) && (
              <div className="form-group">
                <label className="form-label">Paid From Account</label>
                <select className="form-input" value={form.is_payment_source ? 'YES' : 'NO'} onChange={e => setForm(f => ({ ...f, is_payment_source: e.target.value === 'YES' }))}>
                  <option value="NO">No</option>
                  <option value="YES">Yes — usable to pay from (bank, cash, credit card)</option>
                </select>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="btn btn-primary" onClick={handleAdd} disabled={saving}>{saving ? 'Saving…' : 'Add Account'}</button>
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <input className="form-input" placeholder="Search by code, name, category..." value={q} onChange={e => setQ(e.target.value)} style={{ maxWidth: 400 }} />
      </div>

      {loading ? <div className="spinner" /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {categoryOrder.filter(c => grouped[c]).map(cat => (
            <div className="card" key={cat}>
              <div className="card-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className={`badge ${categoryColor[cat]}`}>{cat}</span>
                  <span className="card-title">{cat === 'ASSET' ? 'Assets (1000s)' : cat === 'LIABILITY' ? 'Liabilities (2000s)' : cat === 'EQUITY' ? 'Equity (3000s)' : cat === 'INCOME' ? 'Income (4000s)' : 'Expenses (50006000s)'}</span>
                </div>
                <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{grouped[cat].length} accounts</span>
              </div>
              <div className="table-wrapper">
                <table>
                  <thead><tr><th>Code</th><th>Account Name</th><th>Description</th><th>Status</th><th>Paid From</th><th></th></tr></thead>
                  <tbody>
                    {grouped[cat].map(a => (
                      <tr key={a.id}>
                        <td><span style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--text-muted)' }}>{a.account_code}</span></td>
                        <td style={{ fontWeight: 500 }}>{a.account_name}</td>
                        <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{a.description||''}</td>
                        <td>
                          {a.is_system && <span className="badge badge-purple" style={{ marginRight: 4 }}>System</span>}
                          <span className={`badge ${a.is_active?'badge-green':'badge-gray'}`}>{a.is_active?'Active':'Inactive'}</span>
                        </td>
                        <td>
                          {!canBePaymentSource(a.category, a.account_code) ? (
                            <span style={{ color: '#ccc' }}>—</span>
                          ) : isAdmin ? (
                            <button
                              type="button"
                              onClick={() => setConfirmToggle({ acc: a, next: !a.is_payment_source })}
                              style={{
                                width: 38, height: 20, borderRadius: 10, border: 'none', cursor: 'pointer',
                                background: a.is_payment_source ? 'var(--success, #16a34a)' : 'var(--border, #d1d5db)',
                                position: 'relative', transition: 'background 0.15s', padding: 0,
                              }}
                              title={a.is_payment_source ? 'Usable as Paid From account — click to remove' : 'Not usable as Paid From account — click to enable'}
                            >
                              <span style={{
                                position: 'absolute', top: 2, left: a.is_payment_source ? 20 : 2,
                                width: 16, height: 16, borderRadius: '50%', background: '#fff',
                                transition: 'left 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
                              }} />
                            </button>
                          ) : (
                            <span className={`badge ${a.is_payment_source ? 'badge-green' : 'badge-gray'}`}>{a.is_payment_source ? 'Yes' : 'No'}</span>
                          )}
                        </td>
                        <td><Link to={`/accounts/${a.id}/ledger`} className="btn btn-secondary btn-sm">Ledger</Link></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {confirmToggle && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="card" style={{ width: 440, padding: 24 }}>
            <div className="card-title" style={{ marginBottom: 12 }}>
              {confirmToggle.next ? 'Mark as Paid From Account' : 'Remove as Paid From Account'}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
              {confirmToggle.next ? (
                <>
                  <strong>{confirmToggle.acc.account_code} — {confirmToggle.acc.account_name}</strong> will become selectable in the <strong>Paid From</strong> dropdown on Payment Vouchers (bank, cash, credit card accounts).
                  <div style={{ marginTop: 10 }}>
                    This flag alone does not post any journal entry — it only controls which accounts appear as a payment source. The actual double entry (DR the liability/expense, CR this account) happens when a Payment Voucher is created against a supplier or expense.
                  </div>
                </>
              ) : (
                <>
                  <strong>{confirmToggle.acc.account_code} — {confirmToggle.acc.account_name}</strong> will no longer appear in the <strong>Paid From</strong> dropdown on Payment Vouchers.
                  <div style={{ marginTop: 10 }}>
                    Existing journal entries already posted against this account are not affected — this only changes what's selectable going forward.
                  </div>
                </>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setConfirmToggle(null)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={confirmTogglePaymentSource}>Confirm</button>
            </div>
          </div>
        </div>
      )}
      {PasscodeModal}
    </div>
  );
}
